import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  AccountBalance,
  CheckCircle,
  Description,
  Euro,
  Info,
  Assignment,
  Phone,
  Email
} from '@mui/icons-material';

function JobcenterMove() {
  const requirements = [
    'Genehmigung vom Jobcenter vor dem Umzug',
    'Kostenvoranschlag von mindestens 3 Umzugsunternehmen',
    'Nachweis der Notwendigkeit des Umzugs',
    'Mietvertrag der neuen Wohnung',
    'Angemessenheit der neuen Wohnung bestätigt'
  ];

  const coveredCosts = [
    'Umzugsunternehmen (nach Genehmigung)',
    'Umzugskartons und Verpackungsmaterial',
    'Transportkosten',
    'Umzugshelfer (bei Eigenumzug)',
    'Mietkaution (als Darlehen)',
    'Doppelte Mietzahlungen (in begründeten Fällen)'
  ];

  const steps = [
    {
      label: 'Umzug beim Jobcenter anmelden',
      description: 'Informieren Sie Ihr Jobcenter frühzeitig über Ihre Umzugspläne und die Gründe dafür.',
    },
    {
      label: 'Kostenvoranschläge einholen',
      description: 'Holen Sie mindestens 3 Kostenvoranschläge von verschiedenen Umzugsunternehmen ein.',
    },
    {
      label: 'Antrag auf Kostenübernahme stellen',
      description: 'Reichen Sie die Kostenvoranschläge mit dem Antrag auf Kostenübernahme beim Jobcenter ein.',
    },
    {
      label: 'Auf Genehmigung warten',
      description: 'Warten Sie die schriftliche Genehmigung ab, bevor Sie das Umzugsunternehmen beauftragen.',
    },
    {
      label: 'Umzug durchführen',
      description: 'Nach Genehmigung können Sie den Umzug mit uns durchführen lassen.',
    },
    {
      label: 'Belege einreichen',
      description: 'Reichen Sie alle Rechnungen und Belege beim Jobcenter zur Erstattung ein.',
    }
  ];

  const reasons = [
    'Arbeitsaufnahme in einer anderen Stadt',
    'Gesundheitliche Gründe (mit ärztlichem Attest)',
    'Trennung/Scheidung',
    'Zu hohe Mietkosten der aktuellen Wohnung',
    'Kündigung durch den Vermieter',
    'Familienzusammenführung',
    'Unzumutbare Wohnverhältnisse'
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1f28 0%, #162a34 50%, #1c3440 100%)'
    }}>
      <Container maxWidth="lg" sx={{ pt: 12, pb: 6 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <AccountBalance sx={{ fontSize: 80, color: '#55d6dc', mb: 3 }} />
          <Typography variant="h2" sx={{ color: 'white', fontWeight: 'bold', mb: 3 }}>
            Umzug mit Jobcenter
          </Typography>
          <Typography variant="h5" sx={{ color: '#55d6dc', mb: 2 }}>
            Wir unterstützen Sie bei Ihrem Jobcenter-Umzug
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, maxWidth: 800, mx: 'auto' }}>
            Als zugelassenes Umzugsunternehmen arbeiten wir regelmäßig mit Jobcentern zusammen 
            und kennen alle Anforderungen für eine reibungslose Kostenübernahme.
          </Typography>
        </Box>

        {/* Important Alert */}
        <Alert 
          severity="info" 
          icon={<Info />}
          sx={{ 
            mb: 6,
            bgcolor: 'rgba(85,214,220,0.1)',
            color: 'white',
            '& .MuiAlert-icon': { color: '#55d6dc' }
          }}
        >
          <Typography variant="body1">
            <strong>Wichtig:</strong> Die Kostenübernahme muss VOR dem Umzug vom Jobcenter genehmigt werden. 
            Wir erstellen Ihnen gerne einen Kostenvoranschlag für Ihre Antragstellung.
          </Typography>
        </Alert>

        {/* Process Steps */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 4, fontWeight: 'bold' }}>
            Ablauf eines Jobcenter-Umzugs
          </Typography>
          <Card sx={{ bgcolor: '#1a3a44', p: 3 }}>
            <Stepper orientation="vertical">
              {steps.map((step, index) => (
                <Step key={index} active={true}>
                  <StepLabel
                    StepIconProps={{
                      sx: { 
                        color: '#55d6dc !important',
                        '& .MuiStepIcon-text': { fill: '#0a1f28' }
                      }
                    }}
                  >
                    <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
                      {step.label}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography sx={{ color: 'white', opacity: 0.85 }}>
                      {step.description}
                    </Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Card>
        </Box>

        {/* Two Column Layout */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {/* Requirements */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              bgcolor: '#1a3a44',
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.08)',
              height: '100%'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Assignment sx={{ color: '#ffd700', fontSize: 40 }} />
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    Voraussetzungen
                  </Typography>
                </Stack>
                <List>
                  {requirements.map((req, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#55d6dc' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={req}
                        sx={{ color: 'white', opacity: 0.9 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Covered Costs */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              bgcolor: '#1a3a44',
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.08)',
              height: '100%'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Euro sx={{ color: '#ffd700', fontSize: 40 }} />
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    Übernahmefähige Kosten
                  </Typography>
                </Stack>
                <List>
                  {coveredCosts.map((cost, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#55d6dc' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={cost}
                        sx={{ color: 'white', opacity: 0.9 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Accepted Reasons */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Anerkannte Umzugsgründe
          </Typography>
          <Grid container spacing={2}>
            {reasons.map((reason, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: '#275664',
                    border: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <Description sx={{ color: '#55d6dc' }} />
                  <Typography sx={{ color: 'white', opacity: 0.9 }}>
                    {reason}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Contact Section */}
        <Card sx={{ 
          bgcolor: 'rgba(255,215,0,0.1)', 
          border: '2px solid #ffd700'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" sx={{ color: 'white', mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
              Kostenvoranschlag für Jobcenter
            </Typography>
            <Typography variant="body1" sx={{ color: 'white', mb: 4, textAlign: 'center', opacity: 0.9 }}>
              Wir erstellen Ihnen einen detaillierten Kostenvoranschlag, der alle Anforderungen 
              des Jobcenters erfüllt. Kontaktieren Sie uns für eine schnelle Bearbeitung.
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} sm={6} md={4}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                  <Phone sx={{ color: '#ffd700' }} />
                  <Typography sx={{ color: 'white' }}>
                    030 / 123 456 789
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                  <Email sx={{ color: '#ffd700' }} />
                  <Typography sx={{ color: 'white' }}>
                    jobcenter@hnumzugsservice.de
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Box
                component="button"
                onClick={() => window.location.href = '/contact'}
                style={{
                  backgroundColor: '#55d6dc',
                  color: '#275664',
                  padding: '12px 32px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4bc2c8')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#55d6dc')}
              >
                Kostenvoranschlag anfordern
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default JobcenterMove;